'use client';

import { FC, useRef } from 'react';
import { Content, RichTextField } from '@prismicio/client';
import {
  SliceComponentProps,
  PrismicRichText,
  PrismicLink,
} from '@prismicio/react';
import Image from 'next/image';
import { LeftArrowIcon, RightArrowIcon } from '@/assets';

/**
 * Props for `CardGrid`.
 */
export type CardGridProps = SliceComponentProps<Content.CardGridSlice>;

/**
 * Component for "CardGrid" Slices.
 */
const CardGrid: FC<CardGridProps> = ({ slice }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scroll = (scrollOffset: number) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: scrollOffset,
        behavior: 'smooth',
      });
    }
  };

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="py-8 bg-[#0F423A] overflow-x-hidden relative"
    >
      <div className="container mx-auto px-16">
        <div
          ref={scrollContainerRef}
          className="flex gap-6 overflow-x-auto no-scrollbar -mx-16 px-16"
        >
          {slice.primary.items?.map((item, idx) => (
            <div
              key={idx}
              className="min-w-[300px] rounded-[16px] bg-white/10 shadow-[0_4.325px_64.871px_0_rgba(0,0,0,0.08)] flex flex-col p-6"
            >
              {item.image?.url && (
                <Image
                  src={item.image.url}
                  alt={item.image.alt ?? ''}
                  width={400}
                  height={160}
                  className="rounded-[12px] w-full h-40 object-cover mb-4"
                />
              )}
              <h3 className="text-lg font-semibold mb-2 text-white">
                {Array.isArray(item.title) &&
                item.title.length > 0 &&
                typeof item.title[0] === 'object' ? (
                  <PrismicRichText
                    field={item.title as unknown as RichTextField}
                  />
                ) : typeof item.title === 'string' ? (
                  item.title
                ) : (
                  ''
                )}
              </h3>
              <div className="flex items-center text-sm text-white/80 mb-4 gap-4">
                {item.meta_location && (
                  <span className="flex items-center gap-1">
                    {slice.primary.location_icon?.url && (
                      <Image
                        src={slice.primary.location_icon.url}
                        alt="location"
                        width={16}
                        height={16}
                        className="w-4 h-4"
                      />
                    )}
                    {Array.isArray(item.meta_location) &&
                    item.meta_location.length > 0 &&
                    typeof item.meta_location[0] === 'object' ? (
                      <PrismicRichText
                        field={item.meta_location as unknown as RichTextField}
                      />
                    ) : typeof item.meta_location === 'string' ? (
                      item.meta_location
                    ) : (
                      ''
                    )}
                  </span>
                )}
                {item.meta_type && (
                  <span className="flex items-center gap-1">
                    {slice.primary.time_icon?.url && (
                      <Image
                        src={slice.primary.time_icon.url}
                        alt="type"
                        width={16}
                        height={16}
                        className="w-4 h-4"
                      />
                    )}
                    {Array.isArray(item.meta_type) &&
                    item.meta_type.length > 0 &&
                    typeof item.meta_type[0] === 'object' ? (
                      <PrismicRichText
                        field={item.meta_type as unknown as RichTextField}
                      />
                    ) : typeof item.meta_type === 'string' ? (
                      item.meta_type
                    ) : (
                      ''
                    )}
                  </span>
                )}
              </div>
              {item.action_link && (
                <PrismicLink field={item.action_link} className="mt-auto">
                  <button className="border border-white rounded-full px-6 py-2 text-white hover:bg-white/20 transition">
                    {Array.isArray(slice.primary.button_label) &&
                    slice.primary.button_label.length > 0 &&
                    typeof slice.primary.button_label[0] === 'object' ? (
                      <PrismicRichText
                        field={
                          slice.primary.button_label as unknown as RichTextField
                        }
                      />
                    ) : typeof slice.primary.button_label === 'string' ? (
                      slice.primary.button_label
                    ) : (
                      ''
                    )}
                  </button>
                </PrismicLink>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="absolute top-1/2 -translate-y-1/2 flex justify-between w-full px-4 pointer-events-none">
        <button
          onClick={() => scroll(-300)}
          className="text-white hover:text-white/70 transition-colors pointer-events-auto"
        >
          <LeftArrowIcon />
        </button>
        <button
          onClick={() => scroll(300)}
          className="text-white hover:text-white/70 transition-colors pointer-events-auto"
        >
          <RightArrowIcon />
        </button>
      </div>
    </section>
  );
};

export default CardGrid;
